// ── Container transform helpers — natural expansion/contraction ───────────────
// The container's actual bounding box (left/top/width/height) animates between
// geometries while the content stays at its natural size and is revealed
// progressively (no scaling). The container becomes position:fixed for the
// morph, then returns to its normal flex-centered layout.
// Also provides content-fade helpers for the fade-out → morph → fade-in flow.

export type MorphDone = () => void;

const MORPH_EASE = 'cubic-bezier(.2,0,0,1)';
const BOX_PROPS = ['left', 'top', 'width', 'height'];

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

export function pinBox(el: HTMLElement, rect: DOMRect): void {
  el.style.position = 'fixed';
  el.style.left = `${rect.left}px`;
  el.style.top = `${rect.top}px`;
  el.style.width = `${rect.width}px`;
  el.style.height = `${rect.height}px`;
}

export function releaseBox(el: HTMLElement): void {
  el.style.position = '';
  el.style.left = '';
  el.style.top = '';
  el.style.width = '';
  el.style.height = '';
}

/** Resolve after `ms` (usable for CSS transitions). */
function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/** Animate the element's box from `from` to `to`; returns a promise. */
export function morphBetweenAsync(
  el: HTMLElement,
  from: DOMRect,
  to: DOMRect,
  durationMs: number,
): Promise<void> {
  return new Promise((resolve) => {
    if (prefersReducedMotion() || to.width === 0 || to.height === 0) {
      releaseBox(el);
      resolve();
      return;
    }
    pinBox(el, from);
    el.style.overflow = 'hidden';
    el.style.transition = BOX_PROPS.map((p) => `${p} ${durationMs}ms ${MORPH_EASE}`).join(', ');
    void el.offsetWidth; // reflow so the initial box paints before animating
    pinBox(el, to);
    window.setTimeout(() => {
      el.style.transition = '';
      el.style.overflow = '';
      releaseBox(el);
      resolve();
    }, durationMs + 60);
  });
}

/** Animate a container's content opacity (content.fade) to `to` (0..1). */
export async function fade(el: HTMLElement, to: number, durationMs: number): Promise<void> {
  if (prefersReducedMotion()) return;
  el.style.transition = `opacity ${durationMs}ms ease`;
  el.style.opacity = String(to);
  await wait(durationMs);
  el.style.transition = '';
}

/** Ensure content is rendered visible (opacity 1, no transition). */
export function showContent(el: HTMLElement): void {
  el.style.transition = 'none';
  el.style.opacity = '1';
}

/** Ensure content is fully transparent (no transition). */
export function hideContent(el: HTMLElement): void {
  el.style.transition = 'none';
  el.style.opacity = '0';
}

/**
 * In-flow size morph: animate the element's actual width/height (it stays a
 * centered flex child — no position:fixed, no box translation). The element is
 * assumed to currently be pinned to the old size; it animates to the new size,
 * then its geometry is released back to the natural layout.
 */
export function morphSize(
  el: HTMLElement,
  oldW: number,
  oldH: number,
  newW: number,
  newH: number,
  durationMs: number,
): Promise<void> {
  return new Promise((resolve) => {
    if (prefersReducedMotion() || (Math.abs(oldW - newW) <= 1 && Math.abs(oldH - newH) <= 1)) {
      el.style.width = '';
      el.style.height = '';
      el.style.overflow = '';
      el.style.transition = '';
      resolve();
      return;
    }
    el.style.overflow = 'hidden';
    el.style.width = `${oldW}px`;
    el.style.height = `${oldH}px`;
    el.style.transition = `width ${durationMs}ms ${MORPH_EASE}, height ${durationMs}ms ${MORPH_EASE}`;
    void el.offsetWidth; // reflow so the pinned box paints before animating
    el.style.width = `${newW}px`;
    el.style.height = `${newH}px`;
    window.setTimeout(() => {
      el.style.transition = '';
      el.style.overflow = '';
      el.style.width = '';
      el.style.height = '';
      resolve();
    }, durationMs + 60);
  });
}

/**
 * Synchronously measure the element's natural size (height/width:auto) while
 * keeping it visually pinned to the given size. Because this runs in one
 * synchronous block, the browser never paints the intermediate state — no
 * flicker. The element's inline size is restored to `restoreW`/`restoreH`.
 */
export function measureSize(el: HTMLElement, restoreW: number, restoreH: number): { width: number; height: number } {
  el.style.height = 'auto';
  el.style.width = 'auto';
  const height = el.offsetHeight;
  const width = el.offsetWidth;
  el.style.height = `${restoreH}px`;
  el.style.width = `${restoreW}px`;
  return { width, height };
}

/**
 * Open morph: expand the container from the origin geometry (e.g. the trigger
 * button's box) into its natural centered rect.
 */
export function morphIn(el: HTMLElement, from: DOMRect, durationMs: number, done?: MorphDone): void {
  const to = el.getBoundingClientRect();
  if (prefersReducedMotion() || to.width === 0 || to.height === 0) {
    done?.();
    return;
  }
  void morphBetweenAsync(el, from, to, durationMs).then(() => done?.());
}

/**
 * Close morph: contract the container from its current geometry to `to`
 * (the origin), then fire done — the overlay is removed by the caller.
 */
export function morphTo(el: HTMLElement, to: DOMRect, durationMs: number, done?: MorphDone): void {
  const from = el.getBoundingClientRect();
  if (prefersReducedMotion() || from.width === 0 || from.height === 0) {
    done?.();
    return;
  }
  // On close we do NOT release the box — the element is removed by the caller.
  pinBox(el, from);
  el.style.overflow = 'hidden';
  el.style.transition = BOX_PROPS.map((p) => `${p} ${durationMs}ms ${MORPH_EASE}`).join(', ');
  void el.offsetWidth;
  pinBox(el, to);
  window.setTimeout(() => {
    el.style.transition = '';
    el.style.overflow = '';
    done?.();
  }, durationMs + 60);
}
